import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Send, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => setSubscribed(false), 3000);
      setEmail('');
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!message || !senderEmail || !senderName) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: senderName,
          email: senderEmail,
          message: message,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessageSent(true);
        setTimeout(() => setMessageSent(false), 5000);
        setMessage('');
        setSenderEmail('');
        setSenderName('');
      } else {
        setError(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-[#0f0f0f] to-[#0a0a0a] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
      <div className="absolute bottom-1/4 right-0 w-72 h-72 bg-amber-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Newsletter */}
          <div className="bg-gradient-to-br from-amber-900/20 to-amber-800/10 rounded-3xl p-8 md:p-10 border border-amber-500/20">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 rounded-full px-4 py-2 mb-6">
              <Mail className="w-4 h-4 text-amber-500" />
              <span className="text-amber-500 text-sm font-medium">{t('contact.newsletter')}</span>
            </div>

            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('contact.stayUpdated')}
            </h3>
            <p className="text-gray-400 mb-8">
              {t('contact.newsletterDesc')}
            </p>

            <form onSubmit={handleSubscribe} className="space-y-4">
              <div className="relative">
                <Input
                  type="email"
                  placeholder={t('contact.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-14 pr-14 rounded-xl focus:border-amber-500 focus:ring-amber-500/20"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center hover:bg-amber-600 transition-colors"
                >
                  {subscribed ? (
                    <Check className="w-5 h-5 text-black" />
                  ) : (
                    <Send className="w-5 h-5 text-black" />
                  )}
                </button>
              </div>
              {subscribed && (
                <p className="text-green-500 text-sm">{t('contact.subscribed')}</p>
              )}
            </form>

            {/* Social proof */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-gray-400 text-sm mb-4">{t('contact.joinOwners')}</p>
              <div className="flex -space-x-2">
                {[1, 2, 3, 4, 5].map((_, i) => (
                  <div
                    key={`avatar-${i}`}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 border-2 border-[#0a0a0a] flex items-center justify-center text-black font-bold text-sm"
                  >
                    {String.fromCharCode(65 + i)}
                  </div>
                ))}
                <div className="w-10 h-10 rounded-full bg-amber-500/20 border-2 border-[#0a0a0a] flex items-center justify-center text-amber-500 font-medium text-xs">
                  +9K
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('contact.title').split(' ')[0]} <span className="text-amber-500">{t('contact.title').split(' ').slice(1).join(' ')}</span>
            </h3>
            <p className="text-gray-400 mb-8">
              {t('contact.feedbackWelcome')}
            </p>

            <form onSubmit={handleContact} className="space-y-6">
              <div>
                <Input
                  type="text"
                  placeholder={t('contact.yourName')}
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-14 rounded-xl focus:border-amber-500 focus:ring-amber-500/20"
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder={t('contact.emailAddress')}
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-14 rounded-xl focus:border-amber-500 focus:ring-amber-500/20"
                />
              </div>
              <div>
                <Textarea
                  placeholder={t('contact.yourMessage')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={6}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-xl resize-none focus:border-amber-500 focus:ring-amber-500/20"
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-6 rounded-full w-full transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('contact.sending')}
                  </>
                ) : messageSent ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    {t('contact.messageSent')}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    {t('contact.title')}
                  </>
                )}
              </Button>
              {error && (
                <p className="text-red-500 text-center text-sm">{error}</p>
              )}
            </form>

            {messageSent && (
              <p className="text-green-500 text-center mt-4">
                {t('contact.thankYouMessage')}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
